// frontend/src/components/Login.js
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthShell from './AuthShell';
import api, { storeAuthSession } from '../services/api';
import { useTranslation } from 'react-i18next';
import { extractApiError } from '../utils/apiError';

const Login = ({ setIsAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.successMessage || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/login', { username, password });
      if (response.status === 200) {
        if (response.data?.access_token) {
          storeAuthSession(response.data);
          setIsAuthenticated(true);
          navigate('/dashboard', { replace: true });
        } else {
          setError(t('auth.login.tokenMissing'));
        }
      } else {
        setError(t('auth.login.loginFailed'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(extractApiError(err, t('auth.login.loginFailed')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      eyebrow={t('auth.login.eyebrow')}
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('auth.login.heading')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(223, 232, 241, 0.68)' }}>
            {t('auth.login.body')}
          </Typography>
        </Box>

        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label={t('common.username')}
              placeholder={t('auth.login.usernamePlaceholder')}
              fullWidth
              required
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <TextField
              label={t('common.password')}
              placeholder={t('auth.login.passwordPlaceholder')}
              type="password"
              fullWidth
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              fullWidth
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #58C4DD, #6AB0FF)',
                color: '#06111D',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6fd2e8, #84bcff)',
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: '#06111D' }} /> : t('auth.login.submit')}
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(126, 144, 168, 0.16)' }} />

        <Typography variant="body2" sx={{ color: 'rgba(223, 232, 241, 0.68)' }}>
          {t('auth.login.noAccount')}{' '}
          <MuiLink component={Link} to="/register" underline="hover" color="#58C4DD">
            {t('auth.login.createAccountLink')}
          </MuiLink>
        </Typography>
      </Stack>
    </AuthShell>
  );
};

export default Login;
